import { useForm } from '@mantine/form';
import PropTypes from 'prop-types';

import { Button, SwitchInput } from 'ui';
import {
  SCALING_PRICING_DEFAULT_FIXED_PRICES,
  SCALING_PRICING_DEFAULT_USAGE_PRICES,
  SCALING_PRICING_ITEM_LABELS,
} from 'common/constants';
import { parseDonationAmount } from 'common/utils';

import ContactsNumberSlider from './ContactsNumberSlider';
import SegmentControl from '../segment-control/SegmentControl';

const ScalingPricingSubscriptionItemType = {
  FUNDRAISING_ESSENTIALS_SUITE: 'FUNDRAISING_ESSENTIALS_SUITE',
  AUTOMATED_CULTIVATION: 'AUTOMATED_CULTIVATION',
  DONOR_ACQUISITION: 'DONOR_ACQUISITION',
  DONATION_PROCESSING: 'DONATION_PROCESSING',
  FUNDRAISING_SUPPORT_SPECIALIST: 'FUNDRAISING_SUPPORT_SPECIALIST',
};

const SubscriptionBillingInterval = {
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
};

const initialValues = {
  subscription: {
    databaseStartingSize: 5000,
    items: {
      [ScalingPricingSubscriptionItemType.FUNDRAISING_ESSENTIALS_SUITE]: {
        enabled: true,
        yearlyBasePrice: SCALING_PRICING_DEFAULT_USAGE_PRICES.FUNDRAISING_ESSENTIALS_SUITE * 12,
        monthlyUsagePrice: SCALING_PRICING_DEFAULT_USAGE_PRICES.FUNDRAISING_ESSENTIALS_SUITE,
      },
      [ScalingPricingSubscriptionItemType.AUTOMATED_CULTIVATION]: {
        enabled: true,
        yearlyBasePrice:
          (SCALING_PRICING_DEFAULT_FIXED_PRICES.AUTOMATED_CULTIVATION +
            SCALING_PRICING_DEFAULT_USAGE_PRICES.AUTOMATED_CULTIVATION) *
          12,
        monthlyUsagePrice: SCALING_PRICING_DEFAULT_USAGE_PRICES.AUTOMATED_CULTIVATION,
      },
      [ScalingPricingSubscriptionItemType.DONOR_ACQUISITION]: {
        enabled: false,
        yearlyBasePrice: 300000,
      },
      [ScalingPricingSubscriptionItemType.FUNDRAISING_SUPPORT_SPECIALIST]: {
        enabled: false,
        yearlyBasePrice: SCALING_PRICING_DEFAULT_FIXED_PRICES.FUNDRAISING_SUPPORT_SPECIALIST * 12,
        monthlyUsagePrice: SCALING_PRICING_DEFAULT_USAGE_PRICES.FUNDRAISING_SUPPORT_SPECIALIST,
        contactFloor: '20000',
      },
      [ScalingPricingSubscriptionItemType.DONATION_PROCESSING]: {
        enabled: false,
        yearlyBasePrice: 0,
      },
    },
    interval: SubscriptionBillingInterval.MONTHLY,
  },
};

const BuildPlanForm = ({ onSubmit, onCancel, subscription }) => {
  const form = useForm({
    initialValues: subscription ? { subscription } : initialValues,
  });

  const billingSegments = [
    { segment: SubscriptionBillingInterval.MONTHLY, label: 'Pay Monthly' },
    { segment: SubscriptionBillingInterval.YEARLY, label: 'Pay Yearly' },
  ];

  const orderSubTotal = Object.values(form.values.subscription.items).reduce(
    (acc, item) => acc + (item.enabled ? item.yearlyBasePrice : 0),
    0,
  );

  const displayPrice =
    form.values.subscription.interval === SubscriptionBillingInterval.MONTHLY ? orderSubTotal / 12 : orderSubTotal;
  const priceLabel = form.values.subscription.interval === SubscriptionBillingInterval.MONTHLY ? '/month' : '/year';

  const handleCalculateDefaultPrices = (databaseStartingSize) => {
    for (const [itemType, usagePrice] of Object.entries(SCALING_PRICING_DEFAULT_USAGE_PRICES)) {
      const fixedPrice = SCALING_PRICING_DEFAULT_FIXED_PRICES[itemType] || 0;
      const contactFloor = Number(form.values.subscription.items[itemType].contactFloor);

      if (!contactFloor) {
        const price = ((databaseStartingSize / 5000) * usagePrice + fixedPrice) * 12;
        form.setFieldValue(`subscription.items.${itemType}.yearlyBasePrice`, price);
        continue;
      }

      // Usage pricing does not start for products that have a contact floor until the contact floor is reached
      if (databaseStartingSize <= contactFloor) {
        form.setFieldValue(`subscription.items.${itemType}.yearlyBasePrice`, fixedPrice * 12);
        continue;
      }

      const price = (((databaseStartingSize - contactFloor) / 5000) * usagePrice + fixedPrice) * 12;
      form.setFieldValue(`subscription.items.${itemType}.yearlyBasePrice`, price);
    }
  };

  return (
    <div className="size-full overflow-y-auto py-8">
      <form
        noValidate
        className="flex min-h-full w-full flex-col items-center justify-center"
        onSubmit={(event) => {
          event.stopPropagation();
          form.onSubmit(onSubmit)(event);
        }}
      >
        <div className="flex w-[400px] flex-col items-center space-y-6">
          <h1 className="w-full text-left text-h2 font-semibold">Build Your Plan</h1>

          <div className="space-y-4">
            <ContactsNumberSlider
              value={form.values.subscription.databaseStartingSize}
              onChange={(value) => {
                form.setFieldValue('subscription.databaseStartingSize', value);
                handleCalculateDefaultPrices(value);
              }}
            />

            <div className="space-y-2 rounded-lg border border-primary-700 p-2">
              <span className="text-caption2 font-semibold uppercase">
                {SCALING_PRICING_ITEM_LABELS.FUNDRAISING_ESSENTIALS_SUITE}
              </span>
              <p className="text-sm">
                A platform that allows you to create and manage your contacts, communications, and donation pages.
              </p>
            </div>

            <div className="space-y-2 rounded-lg border border-primary-700 p-2">
              <span className="text-caption2 font-semibold uppercase">
                {SCALING_PRICING_ITEM_LABELS.AUTOMATED_CULTIVATION}
              </span>
              <p className="text-sm">
                Build your donor relationships with an automated system that recommends specific communications and who
                to send them to.
              </p>
            </div>

            <SwitchInput
              label={
                <span className="text-caption2 font-semibold uppercase">
                  {SCALING_PRICING_ITEM_LABELS.DONOR_ACQUISITION}
                </span>
              }
              checked={form.values.subscription.items[ScalingPricingSubscriptionItemType.DONOR_ACQUISITION].enabled}
              onChange={(checked) =>
                form.setFieldValue(
                  `subscription.items.${ScalingPricingSubscriptionItemType.DONOR_ACQUISITION}.enabled`,
                  checked,
                )
              }
              className="w-full flex-row-reverse flex-wrap !content-start !justify-between gap-2 rounded-lg border border-gray-100 p-2 has-[:checked]:border-primary-700"
            >
              <p className="grow text-sm">
                Grow your donor database with automated, targeted outreach to prospective new donors from a pool of over
                20M active donors.
              </p>
            </SwitchInput>
            <SwitchInput
              label={
                <span className="text-caption2 font-semibold uppercase">
                  {SCALING_PRICING_ITEM_LABELS.DONATION_PROCESSING}
                </span>
              }
              checked={form.values.subscription.items[ScalingPricingSubscriptionItemType.DONATION_PROCESSING].enabled}
              onChange={(checked) =>
                form.setFieldValue(
                  `subscription.items.${ScalingPricingSubscriptionItemType.DONATION_PROCESSING}.enabled`,
                  checked,
                )
              }
              className="w-full flex-row-reverse flex-wrap !content-start !justify-between gap-2 rounded-lg border border-gray-100 p-2 has-[:checked]:border-primary-700"
            >
              <p className="grow text-sm">Process donations on your website and with standalone landing pages.</p>
            </SwitchInput>
            <SwitchInput
              label={
                <span className="text-caption2 font-semibold uppercase">
                  {SCALING_PRICING_ITEM_LABELS.FUNDRAISING_SUPPORT_SPECIALIST}
                </span>
              }
              checked={
                form.values.subscription.items[ScalingPricingSubscriptionItemType.FUNDRAISING_SUPPORT_SPECIALIST]
                  .enabled
              }
              onChange={(checked) =>
                form.setFieldValue(
                  `subscription.items.${ScalingPricingSubscriptionItemType.FUNDRAISING_SUPPORT_SPECIALIST}.enabled`,
                  checked,
                )
              }
              className="w-full flex-row-reverse flex-wrap !content-start !justify-between gap-2 rounded-lg border border-gray-100 p-2 has-[:checked]:border-primary-700"
            >
              <p className="grow text-sm">
                A dedicated DonorSpring representative who writes custom copy and supports you in your fundraising.
              </p>
            </SwitchInput>
          </div>

          <div className="w-[250px] space-y-4">
            <SegmentControl
              segments={billingSegments}
              selectedSegment={form.values.subscription.interval}
              onSelect={(segment) => {
                form.setFieldValue('subscription.interval', segment);
              }}
              segmentWidth={125}
              segmentHeight={30}
            />

            <div className="flex items-center justify-between text-base">
              <div>Total:</div>
              <div className="font-semibold">
                {parseDonationAmount(displayPrice / 100)}
                {priceLabel}
              </div>
            </div>
          </div>

          {onCancel ? (
            <div className="flex w-full items-center justify-between space-x-3">
              <Button title="Cancel" onClick={onCancel} />

              <Button title="Confirm" color="primary" type="submit" onClick={() => form.onSubmit(onSubmit)()} />
            </div>
          ) : (
            <Button title="Continue" color="primary" size="xl" className="!block w-full" type="submit" />
          )}
        </div>
      </form>
    </div>
  );
};

BuildPlanForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  subscription: PropTypes.shape({
    databaseStartingSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    items: PropTypes.objectOf(
      PropTypes.shape({
        enabled: PropTypes.bool,
        yearlyBasePrice: PropTypes.number,
        contactFloor: PropTypes.string,
      }),
    ),
  }),
};

export default BuildPlanForm;
